/** Marketing copy aligned with production https://kt.copilot.wecrew.in/#/ */

export const BRAND = {
  product: "Wecrew Copilot",
  tagline: "Enterprise knowledge copilot",
  appHost: "kt.copilot.wecrew.in",
};

export const CONTACT_EMAIL = "info@wecrew.in";
export const CONTACT_PHONE = "+91 91767 72077";
export const CONTACT_PHONE_TEL = "+919176772077";

export const NAV_LINKS = [
  { id: "product", label: "Product", href: "#product" },
  { id: "solutions", label: "Solutions", href: "#solutions" },
  { id: "features", label: "Features", href: "#features" },
  { id: "integrations", label: "Integrations", href: "#platform-integrations" },
  { id: "security", label: "Security", href: "#security" },
  { id: "docs", label: "Documentation", page: "architecture" },
  { id: "contact", label: "Contact", href: "#contact" },
];

export const HERO = {
  eyebrow: "Secure AI copilot for enterprise teams",
  headline: ["Your enterprise knowledge.", "One intelligent copilot."],
  body:
    "Connect your documents, systems, tools, and teams to a secure AI copilot that returns accurate, cited answers, automates routine workflows, and helps employees resolve work faster.",
  proofPoints: [
    "Answers cite the source document and section",
    "Runs on your own cluster — data stays inside your network",
    "Role-based access, audit trail, and API keys built in",
  ],
};

export const AUDIENCE = {
  statement: "Built for teams that need secure, accurate, and actionable AI.",
  teams: [
    "IT Operations",
    "Human Resources",
    "Finance",
    "Customer Support",
    "Sales",
    "Engineering",
    "Knowledge Management",
  ],
};

export const PRODUCT_TOURS = [
  {
    id: "ask",
    label: "Ask Knowledge",
    shot: "chat",
    value:
      "Employees stop hunting through wikis and ticket history. The copilot retrieves the passage that answers the question and shows where it came from.",
    prompt: "What is the rollback procedure for a failed payment-service deploy?",
    answer: [
      "Scale the previous ReplicaSet back to full capacity, then confirm the readiness probe passes before draining the new pods.",
      "Raise a retrospective change record within 24 hours of the rollback.",
    ],
    citations: [
      "payments-runbook.pdf · §4.2 Rollback",
      "change-policy.md · Emergency changes",
    ],
    actions: ["Open the runbook", "Draft the change record"],
  },
  {
    id: "generate",
    label: "Generate Documents",
    shot: "kb",
    value:
      "Turn scattered source material into a first draft that already points at the documents it was built from.",
    prompt: "Draft an onboarding checklist for a new platform engineer.",
    answer: [
      "A seven-day checklist covering access requests, cluster orientation, on-call shadowing, and the first guided change.",
      "Each item links back to the internal policy or runbook it came from.",
    ],
    citations: ["onboarding-guide.pdf · Week one", "access-matrix.xlsx · Platform roles"],
    actions: ["Export as Markdown", "Save to knowledge base"],
  },
  {
    id: "automate",
    label: "Automate Tasks",
    shot: "integrations",
    value:
      "Move from an answer to an action. Connected automation workspaces run the repetitive follow-up work.",
    prompt: "Open a change request for tonight's database maintenance window.",
    answer: [
      "Prepared a change record with the maintenance window, affected services, and the rollback plan from the runbook.",
      "Routed to the automation workspace for approval before submission.",
    ],
    citations: ["db-maintenance.md · Window policy", "change-policy.md · Standard changes"],
    actions: ["Send for approval", "Review payload"],
  },
  {
    id: "analyze",
    label: "Analyze Data",
    shot: "dashboard",
    value:
      "See how the copilot is actually used — volume, latency, token spend, and how often answers were grounded in sources.",
    prompt: "How did copilot usage trend over the last 30 days?",
    answer: [
      "Query volume, p50 and p95 latency, and token usage for the trailing window, compared against the previous window.",
      "Grounding rate shows the share of answers that returned at least one source.",
    ],
    citations: ["Analytics · trailing 30 days"],
    actions: ["Open analytics", "Change window"],
  },
  {
    id: "itsupport",
    label: "IT Support",
    shot: "incidents",
    value:
      "L2 engineers get structured triage steps beside the incident instead of a wall of unstructured chat.",
    prompt: "Checkout latency spiked after the 14:00 release. Where do I start?",
    answer: [
      "Correlated the release window with error-rate and latency signals, then listed the triage steps from the service runbook.",
      "Surfaced the linked change record and the escalation contact.",
    ],
    citations: ["checkout-runbook.pdf · Latency triage", "CHG-2291 · Release record"],
    actions: ["Open incident", "Search related logs"],
  },
  {
    id: "employee",
    label: "Employee Assistance",
    shot: "kb",
    value:
      "Routine policy questions get answered from approved documents, so specialist teams keep their time for real cases.",
    prompt: "How much notice do I need to give before taking parental leave?",
    answer: [
      "Answered from the current published leave policy, including the notice period and the approval path.",
      "Flagged the effective date of the policy version used.",
    ],
    citations: ["leave-policy.pdf · Parental leave"],
    actions: ["Open policy", "Ask a follow-up"],
  },
];

export const TOUR_SHOTS = {
  chat: "/screenshots/console-chat.png",
  logs: "/screenshots/console-logs.png",
  kb: "/screenshots/console-kb.png",
  integrations: "/screenshots/console-integrations.png",
  dashboard: "/screenshots/console-dashboard.png",
  incidents: "/screenshots/console-incidents.png",
};

export const FEATURES = [
  {
    id: "search",
    icon: "search",
    title: "Enterprise knowledge search",
    desc: "Retrieval across every knowledge base your role can reach — no keyword guessing, no dead intranet links.",
  },
  {
    id: "answers",
    icon: "sparkle",
    title: "AI question answering",
    desc: "Grounded responses assembled from retrieved passages, so the copilot answers from your content instead of guessing.",
  },
  {
    id: "documents",
    icon: "document",
    title: "Document intelligence",
    desc: "Upload PDF, Markdown, DOCX, TXT, CSV, or XLSX. Files are parsed, chunked, and embedded for retrieval automatically.",
  },
  {
    id: "workflow",
    icon: "bolt",
    title: "Workflow automation",
    desc: "Trigger automations through connected n8n and Flowise workspaces instead of copying steps between tools.",
  },
  {
    id: "sources",
    icon: "plug",
    title: "Multi-source integration",
    desc: "A catalog of ITSM, collaboration, knowledge, source-control, cloud, and data connectors with scoped credentials.",
  },
  {
    id: "rbac",
    icon: "shield",
    title: "Role-based access control",
    desc: "Owner, admin, editor, and viewer roles decide who can publish knowledge and who can only ask questions.",
  },
  {
    id: "citations",
    icon: "quote",
    title: "Source citations",
    desc: "Every answer returns the documents it used, so reviewers can verify a response before acting on it.",
  },
  {
    id: "history",
    icon: "history",
    title: "Conversation context",
    desc: "Follow-up questions carry prior turns, so the copilot keeps the thread across a troubleshooting session.",
  },
  {
    id: "collab",
    icon: "users",
    title: "Team collaboration",
    desc: "Shared knowledge bases let a team curate one source of truth instead of maintaining private copies.",
  },
  {
    id: "analytics",
    icon: "chart",
    title: "Analytics and insights",
    desc: "Query volume, latency percentiles, token usage, and grounding rate across a trailing window.",
  },
];

export const HOW_STEPS = [
  {
    id: "connect",
    n: "01",
    title: "Connect your enterprise tools",
    desc: "Authorize connectors with scoped credentials. Secrets live in your secret store, never in chat.",
  },
  {
    id: "index",
    n: "02",
    title: "Index organizational knowledge",
    desc: "Documents are parsed, chunked, and embedded into a vector store your deployment controls.",
  },
  {
    id: "ask",
    n: "03",
    title: "Ask questions or trigger actions",
    desc: "Employees ask in plain language. The copilot retrieves the relevant passages before it answers.",
  },
  {
    id: "act",
    n: "04",
    title: "Get trusted answers and outcomes",
    desc: "Responses arrive with citations and next steps — and can hand off to an automation workflow.",
  },
];

export const ARCH_FLOW = [
  {
    id: "sources",
    label: "Data sources",
    icon: "plug",
    items: ["Google Drive", "SharePoint", "Confluence", "Jira", "Slack", "Email", "Databases", "APIs"],
  },
  {
    id: "layer",
    label: "Secure knowledge layer",
    icon: "lock",
    items: ["Parsing", "Chunking", "Embeddings", "Access filters"],
  },
  {
    id: "copilot",
    label: "AI copilot",
    icon: "sparkle",
    items: ["Retrieval", "MCP tools", "Grounded answer", "Citations"],
  },
  {
    id: "users",
    label: "Users and actions",
    icon: "users",
    items: ["Console", "API", "Automations", "Audit trail"],
  },
];

export const DEPARTMENTS = [
  {
    id: "it",
    icon: "server",
    name: "IT and DevOps",
    example: "Troubleshoot an incident using runbooks, change records, and log context in one place.",
  },
  {
    id: "hr",
    icon: "users",
    name: "Human Resources",
    example: "Answer employee policy questions from the current published handbook.",
  },
  {
    id: "finance",
    icon: "chart",
    name: "Finance",
    example: "Summarize a report or retrieve the approval steps for a procurement request.",
  },
  {
    id: "sales",
    icon: "bolt",
    name: "Sales",
    example: "Find product capabilities and account history before a customer call.",
  },
  {
    id: "support",
    icon: "chat",
    name: "Customer Support",
    example: "Draft an accurate reply from internal knowledge, with the source attached.",
  },
  {
    id: "legal",
    icon: "shield",
    name: "Legal and Compliance",
    example: "Locate the controlling clause and the document version it came from.",
  },
  {
    id: "eng",
    icon: "code",
    name: "Engineering",
    example: "Look up service ownership, deployment steps, and architecture decisions.",
  },
  {
    id: "leadership",
    icon: "sparkle",
    name: "Leadership",
    example: "Retrieve summaries and operational insights without chasing a status thread.",
  },
];

export const INTEGRATIONS_PREVIEW = [
  { id: "servicenow", name: "ServiceNow", emoji: "🎫", group: "ITSM" },
  { id: "jsm", name: "Jira Service Management", emoji: "🎯", group: "ITSM" },
  { id: "teams", name: "Microsoft Teams", emoji: "💬", group: "Collaboration" },
  { id: "slack", name: "Slack", emoji: "🟣", group: "Collaboration" },
  { id: "outlook", name: "Outlook / Email", emoji: "📧", group: "Collaboration" },
  { id: "confluence", name: "Confluence", emoji: "📘", group: "Knowledge" },
  { id: "sharepoint", name: "SharePoint", emoji: "📁", group: "Knowledge" },
  { id: "github", name: "GitHub", emoji: "🐙", group: "Source control" },
  { id: "gitlab", name: "GitLab", emoji: "🦊", group: "Source control" },
  { id: "aws", name: "AWS", emoji: "☁️", group: "Cloud" },
  { id: "entra", name: "Azure AD / Entra ID", emoji: "🔐", group: "Identity" },
  { id: "kubernetes", name: "Kubernetes", emoji: "☸️", group: "Cloud" },
  { id: "splunk", name: "Splunk", emoji: "🔍", group: "Observability" },
  { id: "postgres", name: "PostgreSQL / SQL", emoji: "🐘", group: "Data" },
  { id: "webhooks", name: "REST API / Webhooks", emoji: "🔗", group: "Data" },
  { id: "n8n", name: "n8n", emoji: "🔄", group: "Automation" },
  { id: "flowise", name: "Flowise", emoji: "🧠", group: "Automation" },
  { id: "infisical", name: "Infisical", emoji: "🔒", group: "Secrets" },
];

export const MCP_SERVERS = [
  { id: "metrics", name: "metrics-mcp", port: 8001, emoji: "📊", desc: "CPU, memory, pod restarts, queue and OMS latency" },
  { id: "logs-index", name: "logs-index-mcp", port: 8002, emoji: "🔍", desc: "Order lookups, failure patterns, trade summaries" },
  { id: "log-stream", name: "log-stream-mcp", port: 8003, emoji: "📋", desc: "Pod, application, gateway, and crash logs" },
  { id: "kubernetes", name: "kubernetes-mcp", port: 8004, emoji: "☸️", desc: "Pod status, cluster events, node health, deployments" },
  { id: "snmp", name: "snmp-network-mcp", port: 8005, emoji: "🌐", desc: "Switch ports, device CPU, interface traffic, ARP tables" },
  { id: "trading", name: "trading-oms-mcp", port: 8006, emoji: "📈", desc: "Order rejections, exchange latency, broker statistics" },
  { id: "platform-db", name: "platform-db-mcp", port: 8007, emoji: "🗄️", desc: "Device health, SSL expiry, open tickets, site summaries" },
  { id: "runbook", name: "runbook-mcp", port: 8008, emoji: "📖", desc: "Runbook retrieval, SOP lookup, and document ingestion" },
];

export const SECURITY_ITEMS = [
  { id: "rbac", title: "Role-based access control", desc: "Owner, admin, editor, and viewer roles gate who can publish knowledge and who can only read.", planned: false },
  { id: "tokens", title: "Token-based authentication", desc: "Sessions are issued as signed bearer tokens and verified on every API request.", planned: false },
  { id: "keys", title: "Scoped API keys", desc: "Issue and revoke keys for machine access without sharing user credentials.", planned: false },
  { id: "private", title: "Private deployment", desc: "Ships as containers with Kubernetes manifests, so the copilot runs inside your own cluster.", planned: false },
  { id: "tls", title: "Encryption in transit", desc: "Public endpoints terminate TLS with managed certificates; internal traffic stays in-cluster.", planned: false },
  { id: "audit", title: "Audit trail", desc: "Authentication, knowledge, and integration events are recorded for review.", planned: false },
  { id: "citations", title: "Source transparency", desc: "Answers expose the retrieved documents, so a reviewer can check the evidence.", planned: false },
  { id: "secrets", title: "Secure connectors", desc: "Connector credentials are supplied through environment secrets, never pasted into a conversation.", planned: false },
  { id: "sso", title: "Single sign-on", desc: "Azure AD / Entra ID sign-in through the identity connector.", planned: true },
  { id: "permaware", title: "Permission-aware retrieval", desc: "Filtering retrieved passages against each connector's source permissions.", planned: true },
];

export const ROLES = [
  { role: "Owner", scope: "Full control", tone: "own" },
  { role: "Admin", scope: "Manage users, keys, connectors", tone: "adm" },
  { role: "Editor", scope: "Publish and curate knowledge", tone: "edt" },
  { role: "Viewer", scope: "Ask questions, read answers", tone: "vwr" },
];

export const OUTCOMES = [
  { id: "search", title: "Less time searching", desc: "One question replaces a hunt through wikis, drives, and old tickets." },
  { id: "onboarding", title: "Faster onboarding", desc: "New joiners ask the copilot instead of interrupting a senior colleague." },
  { id: "accuracy", title: "More accurate responses", desc: "Answers are assembled from approved documents and shown with their sources." },
  { id: "automation", title: "Fewer repetitive steps", desc: "Routine follow-up work hands off to a connected automation workspace." },
  { id: "load", title: "Lower support load", desc: "Common questions resolve at first contact instead of becoming tickets." },
  { id: "knowledge", title: "Knowledge that persists", desc: "Runbooks and policies stay useful when the person who wrote them moves on." },
  { id: "decisions", title: "Faster decisions", desc: "Operational context arrives in one place instead of across five tools." },
];

export const TESTIMONIALS = [
  {
    id: "t1",
    placeholder: true,
    quote: "Reserved for a customer quote about reducing time spent searching for operational knowledge.",
    name: "Customer name",
    role: "Role",
    org: "Organization",
  },
  {
    id: "t2",
    placeholder: true,
    quote: "Reserved for a customer quote about onboarding new engineers with cited runbook answers.",
    name: "Customer name",
    role: "Role",
    org: "Organization",
  },
  {
    id: "t3",
    placeholder: true,
    quote: "Reserved for a customer quote about keeping enterprise AI inside their own infrastructure.",
    name: "Customer name",
    role: "Role",
    org: "Organization",
  },
];

export const FOOTER_COLS = [
  {
    id: "product",
    title: "Product",
    links: [
      { label: "Overview", href: "#product" },
      { label: "Features", href: "#features" },
      { label: "Ask Copilot", page: "chat" },
      { label: "Knowledge bases", page: "kb" },
      { label: "Analytics", page: "analytics" },
    ],
  },
  {
    id: "solutions",
    title: "Solutions",
    links: [
      { label: "IT and DevOps", href: "#solutions" },
      { label: "Human Resources", href: "#solutions" },
      { label: "Customer Support", href: "#solutions" },
      { label: "Use cases", href: "#product" },
    ],
  },
  {
    id: "resources",
    title: "Resources",
    links: [
      { label: "Platform architecture", page: "architecture" },
      { label: "Integrations", page: "integrations" },
      { label: "Documents", page: "docs" },
      { label: "How it works", href: "#how-it-works" },
    ],
  },
  {
    id: "company",
    title: "Company",
    links: [
      { label: "Contact", href: "#contact" },
      { label: "Security", href: "#security" },
      { label: "Get started", route: "signup" },
      { label: "Sign in", route: "signin" },
    ],
  },
];

export const FOOTER_LEGAL = [
  { label: "Privacy", pending: true },
  { label: "Terms", pending: true },
  { label: "Security overview", href: "#security" },
];

export const HERO_SCENES = [
  {
    id: "runbook",
    question: "Why is checkout latency spiking after the 14:00 release?",
    retrieving: ["checkout-runbook.pdf", "CHG-2291 release record", "platform logs · 13:45–14:30"],
    answer:
      "The 14:00 release raised the connection-pool ceiling below the new worker count, so requests queue at the database. The runbook's first step is to restore the previous pool size and confirm p95 recovers.",
    citations: [
      { doc: "checkout-runbook.pdf", loc: "§3.1 Latency triage" },
      { doc: "CHG-2291", loc: "Release record" },
    ],
    actions: ["Open incident", "Run rollback workflow"],
  },
  {
    id: "policy",
    question: "How much notice is required before parental leave?",
    retrieving: ["leave-policy.pdf", "hr-handbook.docx", "approval-matrix.xlsx"],
    answer:
      "The published leave policy requires 30 days' written notice to the reporting manager, with HR approval recorded before the leave start date.",
    citations: [
      { doc: "leave-policy.pdf", loc: "Parental leave" },
      { doc: "approval-matrix.xlsx", loc: "HR approvals" },
    ],
    actions: ["Open policy", "Start leave request"],
  },
];

export const HERO_CONNECTORS = [
  { id: "confluence", label: "Confluence", emoji: "📘" },
  { id: "servicenow", label: "ServiceNow", emoji: "🎫" },
  { id: "slack", label: "Slack", emoji: "🟣" },
  { id: "sharepoint", label: "SharePoint", emoji: "📁" },
  { id: "postgres", label: "PostgreSQL", emoji: "🐘" },
  { id: "n8n", label: "n8n", emoji: "🔄" },
];

export const SCENE_PHASES = ["typing", "retrieving", "answering", "complete"];
